/**
 * Public-site redirects: the single config for every retired marketing URL.
 *
 * The server answers each `from` with a 301 (server/lib/marketing-redirects.ts),
 * and the SPA router redirects the same paths client-side for in-app
 * navigation. Query strings (UTM tags) are carried over. To add one, append a
 * row and record why in docs/build/redirects.md.
 */
export interface MarketingRedirect {
  from: string;
  to: string;
}

export const MARKETING_REDIRECTS: readonly MarketingRedirect[] = [
  // Retired promo pages (Travis, Oct 3 2026): old prices and founding copy.
  { from: "/coliving-conference", to: "/pricing" },
  { from: "/coliving-conference/signup", to: "/pricing" },
  { from: "/founders-circle", to: "/pricing" },
  { from: "/founder", to: "/pricing" },
  { from: "/waitlist", to: "/pricing" },
  { from: "/thank-you", to: "/pricing" },
  // Phase 2 (F13), approved by Travis Oct 4 2026: the old feature catalog,
  // Certified PM pages and /sms-terms. See docs/build/redirects.md.
  { from: "/features/daily-pulse", to: "/features" },
  { from: "/features/properties", to: "/features" },
  { from: "/features/pipeline", to: "/research" },
  { from: "/features/members", to: "/features" },
  { from: "/features/operations", to: "/features/maintenance" },
  { from: "/features/vendors", to: "/features/maintenance" },
  { from: "/features/revenue", to: "/features/numbers" },
  { from: "/features/connections", to: "/features" },
  { from: "/features/messages/vendor-sms", to: "/features/messages" },
  { from: "/features/messages/padsplit-messenger", to: "/features/messages" },
  { from: "/features/messages/internal-threads", to: "/features/messages" },
  { from: "/features/messages/notifications", to: "/features/messages" },
  { from: "/features/properties/listings-status", to: "/features" },
  { from: "/features/properties/map-view", to: "/features" },
  { from: "/features/properties/zillow-import", to: "/features" },
  { from: "/features/properties/multi-unit", to: "/features" },
  { from: "/features/properties/image-generator", to: "/features" },
  { from: "/features/properties/underwriting-link", to: "/research" },
  { from: "/features/pipeline/market-research", to: "/research" },
  { from: "/features/pipeline/buy-box", to: "/research" },
  { from: "/features/pipeline/underwrite", to: "/research" },
  { from: "/features/pipeline/find-realtor", to: "/research" },
  { from: "/features/pipeline/finance", to: "/research" },
  { from: "/features/pipeline/acquisitions", to: "/research" },
  { from: "/features/pipeline/buy", to: "/research" },
  { from: "/features/pipeline/renovate", to: "/research" },
  { from: "/features/pipeline/furnish", to: "/research" },
  { from: "/features/pipeline/launch", to: "/research" },
  { from: "/features/members/members", to: "/features" },
  { from: "/features/members/vehicles", to: "/features" },
  { from: "/features/members/reviews", to: "/features" },
  { from: "/features/members/booking-requests", to: "/features/leasing" },
  { from: "/features/operations/tasks", to: "/features/maintenance" },
  { from: "/features/operations/service-forms", to: "/features/maintenance" },
  { from: "/features/operations/cleaning-schedule", to: "/features/maintenance" },
  { from: "/features/operations/todos", to: "/features/maintenance" },
  { from: "/features/operations/supplies", to: "/features/maintenance" },
  { from: "/features/operations/trash-service", to: "/features" },
  { from: "/features/operations/pricing-rooms", to: "/features/leasing" },
  { from: "/features/operations/smart-locks", to: "/features/locks" },
  { from: "/features/operations/thermostats", to: "/features/locks" },
  { from: "/features/vendors/vendor-directory", to: "/features/maintenance" },
  { from: "/features/vendors/vendor-payments", to: "/features/maintenance" },
  { from: "/features/vendors/vendor-settings", to: "/features/maintenance" },
  { from: "/features/vendors/vendor-marketplace", to: "/features" },
  { from: "/features/revenue/profit-loss", to: "/features/numbers" },
  { from: "/features/revenue/income", to: "/features/numbers" },
  { from: "/features/revenue/expenses", to: "/features/numbers" },
  { from: "/features/revenue/recurring", to: "/features/numbers" },
  { from: "/features/revenue/statements", to: "/features/numbers" },
  { from: "/features/revenue/collections", to: "/features" },
  { from: "/features/revenue/direct-bookings", to: "/features/leasing" },
  { from: "/features/revenue/third-party-listings", to: "/features/leasing" },
  { from: "/features/revenue/sell", to: "/research" },
  { from: "/features/connections/padsplit-sync", to: "/features" },
  { from: "/features/connections/stripe", to: "/features" },
  { from: "/features/connections/mercury-bank", to: "/features/numbers" },
  { from: "/features/connections/ttlock-sifely", to: "/features/locks" },
  { from: "/certified-property-manager", to: "/property-managers" },
  { from: "/certified-property-managers", to: "/property-managers" },
  { from: "/sms-terms", to: "/terms#sms" },
];

function normalize(pathname: string): string {
  const lower = (pathname || "/").toLowerCase();
  return lower.length > 1 ? lower.replace(/\/+$/, "") : lower;
}

const BY_PATH = new Map(MARKETING_REDIRECTS.map((r) => [normalize(r.from), r.to]));

/** Appends a query string to a target, keeping any `#fragment` last ("/terms#sms" + "?a=1" → "/terms?a=1#sms"). */
export function withQuery(target: string, search: string): string {
  if (!search || search === "?") return target;
  const q = search.startsWith("?") ? search : `?${search}`;
  const hash = target.indexOf("#");
  return hash >= 0 ? `${target.slice(0, hash)}${q}${target.slice(hash)}` : `${target}${q}`;
}

/** Target for a retired path, or null. Matching ignores case and a trailing slash. */
export function findRedirect(pathname: string): string | null {
  return BY_PATH.get(normalize(pathname)) ?? null;
}
